import { useMemo } from 'react'
import { PITCH_LAYOUTS, buildSlots, toCard, type Card_ } from './pitchLayouts'
import type { DreamTeamPlayer } from '../../types'
import FootballPitchShell from './FootballPitchShell'
import PitchPlayerToken from './PitchPlayerToken'

// Read-only pitch used when someone opens a shared lineup.
export default function DreamTeamPitch({
  formation,
  players,
  boardText,
}: {
  formation: string
  players: DreamTeamPlayer[]
  boardText?: string
}) {
  const slots = useMemo(() => buildSlots(formation), [formation])
  const pitchRows = PITCH_LAYOUTS[formation] || PITCH_LAYOUTS['4-4-2']
  const lineup = useMemo(() => {
    const ordered = [...players]
      .sort((a, b) => a.slot_number - b.slot_number)
      .map((tp) => (tp.player ? toCard(tp.player) : null))
      .filter((c): c is Card_ => !!c)
    const map: Record<string, Card_> = {}
    slots.forEach((slot, index) => {
      const card = ordered[index]
      if (card) map[slot.key] = card
    })
    return map
  }, [players, slots])

  const slotAt = useMemo(() => {
    const map: Record<string, (typeof slots)[number]> = {}
    slots.forEach((slot) => {
      map[`${slot.row}-${slot.col}`] = slot
    })
    return map
  }, [slots])

  return (
    <FootballPitchShell boardText={boardText}>
      {pitchRows.map((row, rowIndex) => (
        <div key={rowIndex} className="grid grid-cols-5 gap-1 items-center">
          {[0, 1, 2, 3, 4].map((col) => {
            const slot = slotAt[`${rowIndex}-${col}`]
            if (!slot) return <div key={col} />
            const player = lineup[slot.key]
            if (!player) return <div key={col} />
            return (
              <PitchPlayerToken
                key={col}
                player={player}
                position={slot.position}
                floatKey={rowIndex * 5 + col}
                locked
              />
            )
          })}
        </div>
      ))}
    </FootballPitchShell>
  )
}
